/**
 * RepConnect - Profile Enhancement Storage System
 * 
 * Manages storage, retrieval, and privacy filtering of provider profile enhancements
 * with support for CRM integration and data source hierarchy.
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * Copyright (c) 2025 RepConnect, LLC
 */

import {
  PRIVACY_LEVELS,
  DATA_SOURCES,
  ENHANCEMENT_FIELDS,
  createEnhancement
} from './profileEnhancements.js';
import { logger } from './logger';

const ENHANCEMENTS_KEY = 'meetingsync_profile_enhancements';
const ENHANCEMENT_HISTORY_KEY = 'meetingsync_enhancement_history';

export const ProfileEnhancementStorage = {
  
  /**
   * Get all enhancements (admin/debug use only)
   */
  getAllEnhancements() {
    try {
      const enhancements = localStorage.getItem(ENHANCEMENTS_KEY);
      return enhancements ? JSON.parse(enhancements) : [];
    } catch (error) {
      logger.error('Error getting all enhancements', { error: error.message });
      return [];
    }
  },

  /**
   * Get enhancements for a specific provider with privacy filtering
   * @param {string} providerId - Provider ID
   * @param {string} viewerId - User requesting the data
   * @param {string} viewerType - 'sales' or 'customer'
   * @param {string} viewerOrg - Viewer's organization ID
   * @param {string} profileOwnerId - Provider's user account ID (if they have one)
   * @returns {Object} Filtered enhancements grouped by field
   */
  getProviderEnhancements(providerId, viewerId, viewerType = 'sales', viewerOrg = null, profileOwnerId = null) {
    try {
      const allEnhancements = this.getAllEnhancements();
      const providerEnhancements = allEnhancements.filter(e => e.providerId === providerId);
      
      // Filter by privacy level
      const visibleEnhancements = providerEnhancements.filter(enhancement => {
        return this.canViewEnhancement(enhancement, viewerId, viewerType, viewerOrg, profileOwnerId);
      });

      // Group by field and get highest priority value for each
      const grouped = {};
      
      visibleEnhancements.forEach(enhancement => {
        const fieldName = enhancement.fieldName;
        
        if (!grouped[fieldName]) {
          grouped[fieldName] = [];
        }
        
        grouped[fieldName].push(enhancement);
      });

      // For each field, sort by data source priority and take the highest
      const result = {};
      Object.keys(grouped).forEach(fieldName => {
        const enhancements = grouped[fieldName];
        
        // Sort by data source priority (highest first) then by creation date (newest first)
        enhancements.sort((a, b) => {
          const aPriority = this.getDataSourcePriority(a.dataSource);
          const bPriority = this.getDataSourcePriority(b.dataSource);
          
          if (aPriority !== bPriority) {
            return bPriority - aPriority; // Higher priority first
          }
          
          return new Date(b.createdAt) - new Date(a.createdAt); // Newer first
        });

        result[fieldName] = {
          current: enhancements[0], // Highest priority/newest
          alternatives: enhancements.slice(1), // Other options
          history: this.getFieldHistory(providerId, fieldName, viewerId, viewerType, viewerOrg)
        };
      });

      return result;
    } catch (error) {
      logger.error('Error getting provider enhancements', { error: error.message, providerId });
      return {};
    }
  },

  /**
   * Add a new enhancement
   * @param {string} providerId - Provider ID
   * @param {string} fieldName - Field being enhanced
   * @param {any} value - Enhancement value
   * @param {string} contributorId - User adding the enhancement
   * @param {string} privacyLevel - Privacy level override
   * @returns {Object} Created enhancement
   */
  addEnhancement(providerId, fieldName, value, contributorId, privacyLevel = null) {
    try {
      const enhancement = createEnhancement(providerId, fieldName, value, contributorId, privacyLevel);
      
      const allEnhancements = this.getAllEnhancements();
      allEnhancements.push(enhancement);
      
      localStorage.setItem(ENHANCEMENTS_KEY, JSON.stringify(allEnhancements));
      
      // Log to history
      this.logEnhancementHistory({
        action: 'created',
        enhancementId: enhancement.id,
        providerId,
        fieldName,
        value,
        contributorId,
        timestamp: enhancement.createdAt
      });

      return enhancement;
    } catch (error) {
      logger.error('Error adding enhancement', { error: error.message, providerId, fieldName });
      throw error;
    }
  },

  /**
   * Update an existing enhancement
   * @param {string} enhancementId - Enhancement to update
   * @param {Object} updates - Fields to update
   * @param {string} updaterId - User making the update
   * @returns {Object} Updated enhancement
   */
  updateEnhancement(enhancementId, updates, updaterId) {
    try {
      const allEnhancements = this.getAllEnhancements();
      const enhancementIndex = allEnhancements.findIndex(e => e.id === enhancementId);
      
      if (enhancementIndex === -1) {
        throw new Error('Enhancement not found');
      }

      const currentEnhancement = allEnhancements[enhancementIndex];
      
      // Check if user can edit this enhancement
      if (currentEnhancement.contributorId !== updaterId) {
        throw new Error('Not authorized to edit this enhancement');
      }

      const updatedEnhancement = {
        ...currentEnhancement,
        ...updates,
        updatedAt: new Date().toISOString(),
        version: currentEnhancement.version + 1
      };

      allEnhancements[enhancementIndex] = updatedEnhancement;
      localStorage.setItem(ENHANCEMENTS_KEY, JSON.stringify(allEnhancements));

      // Log to history
      this.logEnhancementHistory({
        action: 'updated',
        enhancementId,
        providerId: currentEnhancement.providerId,
        fieldName: currentEnhancement.fieldName,
        oldValue: currentEnhancement.value,
        newValue: updates.value || currentEnhancement.value,
        contributorId: updaterId,
        timestamp: updatedEnhancement.updatedAt
      });

      return updatedEnhancement;
    } catch (error) {
      logger.error('Error updating enhancement', { error: error.message, enhancementId });
      throw error;
    }
  },

  /**
   * Delete an enhancement
   * @param {string} enhancementId - Enhancement to delete
   * @param {string} deleterId - User deleting
   * @returns {boolean} Success status
   */
  deleteEnhancement(enhancementId, deleterId) {
    try {
      const allEnhancements = this.getAllEnhancements();
      const enhancementIndex = allEnhancements.findIndex(e => e.id === enhancementId);
      
      if (enhancementIndex === -1) {
        return false;
      }

      const enhancement = allEnhancements[enhancementIndex];
      
      // Check if user can delete this enhancement
      if (enhancement.contributorId !== deleterId) {
        throw new Error('Not authorized to delete this enhancement');
      }

      allEnhancements.splice(enhancementIndex, 1);
      localStorage.setItem(ENHANCEMENTS_KEY, JSON.stringify(allEnhancements));

      // Log to history
      this.logEnhancementHistory({
        action: 'deleted',
        enhancementId,
        providerId: enhancement.providerId,
        fieldName: enhancement.fieldName,
        value: enhancement.value,
        contributorId: deleterId,
        timestamp: new Date().toISOString()
      });

      return true;
    } catch (error) {
      logger.error('Error deleting enhancement', { error: error.message, enhancementId });
      throw error;
    }
  },

  /**
   * Check if viewer can see an enhancement based on privacy settings
   * @param {Object} enhancement - Enhancement object
   * @param {string} viewerId - User viewing
   * @param {string} viewerType - 'sales' or 'customer'
   * @param {string} viewerOrg - Viewer's organization
   * @param {string} profileOwnerId - ID of the profile owner (healthcare provider)
   * @returns {boolean} Can view
   */
  canViewEnhancement(enhancement, viewerId, viewerType, viewerOrg, profileOwnerId = null) {
    const { privacyLevel, contributorId } = enhancement;

    // Profile owner can always see all enhancements about themselves
    if (profileOwnerId && viewerId === profileOwnerId) {
      return true;
    }

    switch (privacyLevel) {
      case PRIVACY_LEVELS.PUBLIC:
        return true;

      case PRIVACY_LEVELS.PROFESSIONAL:
        // Healthcare professionals, the contributor, and profile owner can see
        return viewerType === 'customer' || contributorId === viewerId || viewerId === profileOwnerId;

      case PRIVACY_LEVELS.PRIVATE:
        // Only the contributor can see (unless they're the profile owner)
        return contributorId === viewerId;

      case PRIVACY_LEVELS.TEAM:
        // Contributor and same organization members can see
        return contributorId === viewerId || (viewerOrg && viewerOrg === enhancement.contributorOrg);

      default:
        return false;
    }
  },

  /**
   * Get data source priority for sorting
   * @param {string} dataSource - Data source type
   * @returns {number} Priority number
   */
  getDataSourcePriority(dataSource) {
    const source = Object.values(DATA_SOURCES).find(s => s.type === dataSource);
    return source ? source.priority : 0;
  },

  /**
   * Create enhancement from meeting data
   * @param {string} providerId - Provider ID
   * @param {Object} meetingData - Meeting outcome data
   * @param {string} contributorId - User who conducted meeting
   * @returns {Array} Created enhancements
   */
  addMeetingDerivedEnhancements(providerId, meetingData, contributorId) {
    const enhancements = [];

    try {
      // Extract preferences from meeting data
      if (meetingData.restaurantPreference && meetingData.restaurantRating >= 4) {
        const enhancement = {
          ...createEnhancement(providerId, 'preferredRestaurants', [meetingData.restaurant], contributorId),
          dataSource: DATA_SOURCES.MEETING_DERIVED.type,
          derivedFrom: {
            meetingId: meetingData.id,
            confidence: meetingData.restaurantRating / 5,
            source: 'meeting_feedback'
          }
        };
        enhancements.push(enhancement);
      }

      if (meetingData.timePreference) {
        const enhancement = {
          ...createEnhancement(providerId, 'preferredMeetingTime', meetingData.timePreference, contributorId),
          dataSource: DATA_SOURCES.MEETING_DERIVED.type,
          derivedFrom: {
            meetingId: meetingData.id,
            confidence: 0.8,
            source: 'meeting_scheduling'
          }
        };
        enhancements.push(enhancement);
      }

      // Save all enhancements
      const allEnhancements = this.getAllEnhancements();
      allEnhancements.push(...enhancements);
      localStorage.setItem(ENHANCEMENTS_KEY, JSON.stringify(allEnhancements));

      return enhancements;
    } catch (error) {
      logger.error('Error adding meeting-derived enhancements', { error: error.message, providerId });
      return [];
    }
  },

  /**
   * Get enhancement history for a field
   * @param {string} providerId - Provider ID
   * @param {string} fieldName - Field name
   * @param {string} viewerId - User viewing
   * @param {string} viewerType - Viewer type
   * @param {string} viewerOrg - Viewer organization
   * @returns {Array} History entries
   */
  getFieldHistory(providerId, fieldName, viewerId, viewerType, viewerOrg) {
    try {
      const history = localStorage.getItem(ENHANCEMENT_HISTORY_KEY);
      const allHistory = history ? JSON.parse(history) : [];
      
      return allHistory
        .filter(entry => 
          entry.providerId === providerId && 
          entry.fieldName === fieldName &&
          this.canViewHistoryEntry(entry, viewerId, viewerType, viewerOrg)
        )
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    } catch (error) {
      logger.error('Error getting field history', { error: error.message, providerId, fieldName });
      return [];
    }
  },

  /**
   * Check if user can view history entry
   * @param {Object} entry - History entry
   * @param {string} viewerId - Viewer ID
   * @param {string} viewerType - Viewer type
   * @param {string} viewerOrg - Viewer organization
   * @returns {boolean} Can view
   */
  canViewHistoryEntry(entry, viewerId, viewerType, viewerOrg) {
    // For now, only show history to the contributor
    // This could be expanded based on privacy settings
    return entry.contributorId === viewerId;
  },

  /**
   * Log enhancement history
   * @param {Object} historyEntry - History entry data
   */
  logEnhancementHistory(historyEntry) {
    try {
      const history = localStorage.getItem(ENHANCEMENT_HISTORY_KEY);
      const allHistory = history ? JSON.parse(history) : [];
      
      const entry = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        ...historyEntry
      };
      
      allHistory.push(entry);
      localStorage.setItem(ENHANCEMENT_HISTORY_KEY, JSON.stringify(allHistory));
    } catch (error) {
      logger.error('Error logging enhancement history', { error: error.message });
    }
  },

  /**
   * Export provider enhancements for CRM
   * @param {string} providerId - Provider ID
   * @param {string} userId - User requesting export
   * @param {string} format - CRM format ('salesforce', 'hubspot', 'pipedrive')
   * @returns {Object} CRM-formatted data
   */
  exportForCRM(providerId, userId, format = 'generic') {
    try {
      const enhancements = this.getProviderEnhancements(providerId, userId);
      const exported = {
        providerId,
        exportedAt: new Date().toISOString(),
        exportedBy: userId,
        format,
        data: {}
      };

      // Convert enhancements to CRM format
      Object.keys(enhancements).forEach(fieldName => {
        const enhancement = enhancements[fieldName].current;
        if (enhancement && enhancement.privacyLevel !== PRIVACY_LEVELS.PRIVATE) {
          exported.data[fieldName] = {
            value: enhancement.value,
            source: enhancement.dataSource,
            lastUpdated: enhancement.updatedAt,
            verified: enhancement.verified
          };
        }
      });

      return exported;
    } catch (error) {
      logger.error('Error exporting for CRM', { error: error.message, providerId, format });
      return null;
    }
  },

  /**
   * Get enhancement statistics for a user
   * @param {string} userId - User ID
   * @returns {Object} Statistics
   */
  getContributionStats(userId) {
    try {
      const allEnhancements = this.getAllEnhancements();
      const userEnhancements = allEnhancements.filter(e => e.contributorId === userId);

      const stats = {
        totalContributions: userEnhancements.length,
        byCategory: {},
        byPrivacyLevel: {},
        recentActivity: userEnhancements
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 10)
      };

      userEnhancements.forEach(enhancement => {
        // Count by category
        const category = enhancement.category;
        stats.byCategory[category] = (stats.byCategory[category] || 0) + 1;

        // Count by privacy level
        const privacy = enhancement.privacyLevel;
        stats.byPrivacyLevel[privacy] = (stats.byPrivacyLevel[privacy] || 0) + 1;
      });

      return stats;
    } catch (error) {
      logger.error('Error getting contribution stats', { error: error.message, userId });
      return null;
    }
  },

  /**
   * Check if user can edit/moderate enhancements for a provider
   * @param {string} providerId - Provider ID
   * @param {string} userId - User requesting edit access
   * @param {string} userType - 'sales' or 'customer'
   * @param {string} profileOwnerId - Provider's user account ID
   * @returns {Object} Edit permissions
   */
  getEditPermissions(providerId, userId, userType, profileOwnerId = null) {
    const permissions = {
      canAddEnhancements: false,
      canEditOwnEnhancements: false,
      canModerateEnhancements: false,
      canVerifyEnhancements: false,
      allowedCategories: [],
      allowedPrivacyLevels: []
    };

    // Profile owner (healthcare provider) permissions
    if (profileOwnerId && userId === profileOwnerId) {
      permissions.canAddEnhancements = true;
      permissions.canEditOwnEnhancements = true;
      permissions.canModerateEnhancements = true; // Can approve/reject others' contributions
      permissions.canVerifyEnhancements = true;   // Can verify their own info
      permissions.allowedCategories = ['contact', 'office', 'preferences', 'professional'];
      permissions.allowedPrivacyLevels = [PRIVACY_LEVELS.PUBLIC, PRIVACY_LEVELS.PROFESSIONAL];
    }
    // Sales rep permissions
    else if (userType === 'sales') {
      permissions.canAddEnhancements = true;
      permissions.canEditOwnEnhancements = true;
      permissions.allowedCategories = ['contact', 'preferences', 'relationship'];
      permissions.allowedPrivacyLevels = [PRIVACY_LEVELS.PUBLIC, PRIVACY_LEVELS.PROFESSIONAL, PRIVACY_LEVELS.PRIVATE];
    }
    // Healthcare professional viewing another provider
    else if (userType === 'customer') {
      permissions.canAddEnhancements = true;
      permissions.canEditOwnEnhancements = true;
      permissions.allowedCategories = ['contact', 'professional'];
      permissions.allowedPrivacyLevels = [PRIVACY_LEVELS.PUBLIC, PRIVACY_LEVELS.PROFESSIONAL];
    }

    return permissions;
  },

  /**
   * Add enhancement with permission checking
   * @param {string} providerId - Provider ID
   * @param {string} fieldName - Field being enhanced
   * @param {any} value - Enhancement value
   * @param {string} contributorId - User adding the enhancement
   * @param {string} privacyLevel - Privacy level override
   * @param {string} profileOwnerId - Provider's user account ID
   * @param {string} contributorType - 'sales' or 'customer'
   * @returns {Object} Created enhancement
   */
  addEnhancementWithPermissions(providerId, fieldName, value, contributorId, privacyLevel = null, profileOwnerId = null, contributorType = 'sales') {
    try {
      const permissions = this.getEditPermissions(providerId, contributorId, contributorType, profileOwnerId);
      
      if (!permissions.canAddEnhancements) {
        throw new Error('Not authorized to add enhancements');
      }

      const field = ENHANCEMENT_FIELDS[fieldName];
      if (!field) {
        throw new Error(`Unknown enhancement field: ${fieldName}`);
      }

      if (!permissions.allowedCategories.includes(field.category)) {
        throw new Error(`Not authorized to edit ${field.category} fields`);
      }

      const finalPrivacy = privacyLevel || field.defaultPrivacy;
      if (!permissions.allowedPrivacyLevels.includes(finalPrivacy)) {
        throw new Error(`Not authorized to use privacy level: ${finalPrivacy}`);
      }

      // Provider-contributed enhancements get higher data source priority
      let dataSource = DATA_SOURCES.USER_CONTRIBUTED.type;
      if (profileOwnerId && contributorId === profileOwnerId) {
        dataSource = DATA_SOURCES.PROVIDER_VERIFIED.type;
      }

      const enhancement = createEnhancement(providerId, fieldName, value, contributorId, finalPrivacy);
      enhancement.dataSource = dataSource;
      enhancement.contributorType = contributorType;
      enhancement.verified = (contributorId === profileOwnerId); // Auto-verify if provider adds it

      const allEnhancements = this.getAllEnhancements();
      allEnhancements.push(enhancement);
      localStorage.setItem(ENHANCEMENTS_KEY, JSON.stringify(allEnhancements));
      
      this.logEnhancementHistory({
        action: 'created',
        enhancementId: enhancement.id,
        providerId,
        fieldName,
        value,
        contributorId,
        contributorType,
        dataSource,
        timestamp: enhancement.createdAt
      });

      return enhancement;
    } catch (error) {
      logger.error('Error adding enhancement with permissions', { error: error.message, providerId, fieldName });
      throw error;
    }
  },

  /**
   * Verify an enhancement (provider can verify info about themselves)
   * @param {string} enhancementId - Enhancement to verify
   * @param {string} verifierId - User doing the verification
   * @param {string} profileOwnerId - Provider's user account ID
   * @returns {Object} Updated enhancement
   */
  verifyEnhancement(enhancementId, verifierId, profileOwnerId) {
    try {
      // Only the profile owner can verify enhancements about themselves
      if (!profileOwnerId || verifierId !== profileOwnerId) {
        throw new Error('Only the profile owner can verify enhancements');
      }

      const allEnhancements = this.getAllEnhancements();
      const enhancementIndex = allEnhancements.findIndex(e => e.id === enhancementId);
      
      if (enhancementIndex === -1) {
        throw new Error('Enhancement not found');
      }

      const enhancement = allEnhancements[enhancementIndex];
      
      // Update verification status
      allEnhancements[enhancementIndex] = {
        ...enhancement,
        verified: true,
        verifiedBy: verifierId,
        verifiedAt: new Date().toISOString(),
        dataSource: DATA_SOURCES.PROVIDER_VERIFIED.type, // Upgrade to verified source
        updatedAt: new Date().toISOString()
      };

      localStorage.setItem(ENHANCEMENTS_KEY, JSON.stringify(allEnhancements));

      this.logEnhancementHistory({
        action: 'verified',
        enhancementId,
        providerId: enhancement.providerId,
        fieldName: enhancement.fieldName,
        verifierId,
        timestamp: new Date().toISOString()
      });

      return allEnhancements[enhancementIndex];
    } catch (error) {
      logger.error('Error verifying enhancement', { error: error.message, enhancementId });
      throw error;
    }
  },

  /**
   * Get enhancements that need provider verification
   * @param {string} providerId - Provider ID
   * @param {string} profileOwnerId - Provider's user account ID
   * @returns {Array} Unverified enhancements
   */
  getUnverifiedEnhancements(providerId, profileOwnerId) {
    try {
      if (!profileOwnerId) return [];

      const allEnhancements = this.getAllEnhancements();
      return allEnhancements.filter(e => 
        e.providerId === providerId && 
        !e.verified && 
        e.contributorId !== profileOwnerId && // Exclude their own contributions
        e.privacyLevel !== PRIVACY_LEVELS.PRIVATE // Don't show private notes
      );
    } catch (error) {
      logger.error('Error getting unverified enhancements', { error: error.message, providerId });
      return [];
    }
  },

  /**
   * Clear all enhancement data (admin function)
   */
  clearAllData() {
    localStorage.removeItem(ENHANCEMENTS_KEY);
    localStorage.removeItem(ENHANCEMENT_HISTORY_KEY);
  }
};

export default ProfileEnhancementStorage;